// src/composables/useOnboardingFinalize.ts
import { HOME_PATH } from '@/router/paths';
import { useOnboardingDraftStore } from '@/stores/onboardingDraft'
import { useJoinFlowStore } from '@/stores/joinFlow'
import { onboardingCommitService } from '@/services/supabase/onboarding-commit.service'
import { supabaseHouseholdService } from '@/services/supabase/household.service'
import { supabase } from '@/lib/supabase'
import { firstMissingDraftStep, type MissingDraftStep } from '@/types/onboarding'
import { useAnalytics } from '@/composables/useAnalytics'

export type FinalizeResult =
  | { kind: 'joined' | 'created' | 'existing'; householdId: string }
  | { kind: 'incomplete'; missingStep: MissingDraftStep }

export function useOnboardingFinalize() {
  const draftStore = useOnboardingDraftStore()
  const joinFlow = useJoinFlowStore()
  const analytics = useAnalytics()

  /**
   * Le commit est le seul moment où le foyer local devient réel. Son échec
   * ressortait jusqu'ici en `signup_failed`, confondu avec un échec
   * d'authentification — deux problèmes très différents à corriger.
   */
  async function commitDraftTracked(draft: NonNullable<typeof draftStore.draft>) {
    try {
      const result = await onboardingCommitService.commitDraft(draft)
      // Propriété utilisateur posée au commit, le seul passage commun aux quatre
      // chemins de création (HARMONIA-94). Sans réponse, on n'écrit rien.
      if (draft.declaredChildrenCount != null) {
        void analytics.setUserProperty('declared_children_count', draft.declaredChildrenCount)
      }
      return result
    } catch (err: any) {
      void analytics.logDraftCommitFailed(err?.message || 'unknown')
      throw err
    }
  }

  /** Appelé juste APRÈS une auth réussie (OTP ou social). Fork à 4 branches. */
  async function finalizeAfterAuth(): Promise<FinalizeResult> {
    // 1) GARDE-FOU : un joiner ne crée JAMAIS de household.
    if (joinFlow.invitationCode && joinFlow.invitationCode.trim() !== '') {
      const household = await supabaseHouseholdService.acceptInvitation(joinFlow.invitationCode)
      await draftStore.clear() // un joiner n'a pas de brouillon à committer
      // Ferme l'entonnoir co-parent ouvert par household_invite_sent, qui
      // n'avait jusqu'ici aucune contrepartie côté acceptation.
      void analytics.logHouseholdInviteAccepted(household.id, 'code')
      void analytics.logHouseholdJoined(household.id, 'invitation_code')
      return { kind: 'joined', householdId: household.id }
    }

    // 2) COMPTE EXISTANT : l'user a déjà un foyer -> on ouvre l'existant, on ignore le brouillon.
    const existing = await supabaseHouseholdService.getUserHouseholds()
    if (Array.isArray(existing) && existing.length > 0) {
      await draftStore.clear()
      return { kind: 'existing', householdId: existing[0].id }
    }

    // 3) GARDE DE COMPLÉTUDE : ne JAMAIS committer un brouillon partiel (ex. reprise
    //    de parcours ayant sauté une étape -> foyer sans récompenses). On renvoie
    //    l'étape manquante ; l'appelant route l'utilisateur (déjà authentifié) vers
    //    cette étape, puis le commit se refera avec un brouillon complet.
    if (!draftStore.draft) await draftStore.load()
    const missingStep = firstMissingDraftStep(draftStore.draft)
    if (missingStep) {
      return { kind: 'incomplete', missingStep }
    }

    // 4) CRÉATEUR : commit du brouillon local (complet).
    draftStore.ensureCommitId()
    const result = await commitDraftTracked(draftStore.draft!)
    draftStore.markCommitted()
    await draftStore.clear()
    return { kind: 'created', householdId: result.householdId }
  }

  /**
   * Retour d'authentification par un canal EXTERNE à l'écran compte : lien
   * profond natif (OAuth social ouvert dans le navigateur) ou page /auth/callback
   * sur le web.
   *
   * Ces chemins ne repassent jamais par le handler de l'écran compte : la
   * promesse de signInWithGoogle() n'y revient pas, donc handleFinalize() n'y est
   * jamais exécuté. C'est donc ici qu'il faut committer le brouillon et appeler
   * finish(). Sans ça, le foyer n'était créé qu'au démarrage suivant (par
   * finalizePendingCommit) et surtout l'état d'onboarding restait gravé à
   * l'étape « compte » pour toujours : bande de la chouette « créez votre
   * compte » à demeure et retour sur /welcome à chaque lancement, alors que le
   * compte ET le foyer existaient. Cf. le retour client Labrousse du 19/08/2026.
   *
   * Retourne true quand la navigation a été prise en charge (aucun onboarding en
   * cours → false, l'appelant poursuit sa propre logique de redirection).
   */
  async function finalizeAfterExternalAuth(): Promise<boolean> {
    const { Preferences } = await import('@capacitor/preferences')
    const { value: onboardingState } = await Preferences.get({ key: 'harmonia_onboarding' })
    if (!onboardingState) return false

    const { useOnboardingController } = await import('@/composables/useOnboardingController')
    const onboarding = useOnboardingController()
    await onboarding.init()

    try {
      const res = await finalizeAfterAuth()
      // Brouillon incomplet (parcours repris en sautant une étape) : on ne
      // commit pas un foyer partiel, l'utilisateur complète l'étape manquante.
      if (res.kind === 'incomplete') {
        await onboarding.goToStep(res.missingStep)
        return true
      }
    } catch (err) {
      // Commit KO (réseau) : on GARDE l'état d'onboarding et on repose
      // l'utilisateur sur son étape. Il peut réessayer, et au pire le démarrage
      // suivant rattrape le brouillon (finalizePendingCommit) puis purge l'état
      // devenu fantôme (discardGhostOnboarding).
      console.error('[Onboarding] finalize after external auth failed:', err)
      const router = (await import('@/router/index')).default
      router.replace(onboarding.expectedRoute() ?? HOME_PATH)
      return true
    }

    // finish() purge l'état, recharge le contexte et pose l'utilisateur sur le
    // dashboard : c'est la seule sortie propre de l'onboarding.
    await onboarding.finish()
    return true
  }

  /** Self-healing au boot : auth OK mais commit non finalisé. */
  async function finalizePendingCommit(): Promise<void> {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session?.user || session.user.is_anonymous) return
    await draftStore.load()
    const draft = draftStore.draft
    if (!draft) return
    if (draft.committedAt) { await draftStore.clear(); return } // commit OK, purge ratée précédemment
    if (joinFlow.invitationCode && joinFlow.invitationCode.trim() !== '') { await draftStore.clear(); return } // joiner
    // Si l'user a déjà un foyer (ex. commit précédent réussi côté serveur mais committedAt pas posé), ne pas dupliquer.
    const existing = await supabaseHouseholdService.getUserHouseholds()
    if (Array.isArray(existing) && existing.length > 0) { await draftStore.clear(); return }
    // GARDE DE COMPLÉTUDE : ne pas committer un brouillon partiel au boot. On le
    // CONSERVE (pas de purge) : l'utilisateur reprendra l'onboarding et complétera
    // l'étape manquante, puis finalizeAfterAuth committera le brouillon complet.
    if (firstMissingDraftStep(draft)) return
    draftStore.ensureCommitId()
    await commitDraftTracked(draft) // idempotent côté RPC
    draftStore.markCommitted()
    await draftStore.clear()
  }

  return { finalizeAfterAuth, finalizeAfterExternalAuth, finalizePendingCommit }
}
